/**
 * useShortcutBrowseState - Manages search, filter, and merged shortcuts for the browse view.
 */

import {
  getShortcutsForMarket,
  resolveShortcuts,
  type BrowseFilterCategory,
  type ResolvedShortcut,
} from '@/components/ui/floating-ask-heidi/shortcut-marketplace-data';
import Fuse from 'fuse.js';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type { PinnedChip } from './evidence-settings-popover';

export function useShortcutBrowseState(
  customShortcuts?: PinnedChip[],
  pinnedShortcutIds?: Set<string>,
  pinnedShortcutLabels?: Set<string>,
  isUS = true
) {
  const intl = useIntl();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<BrowseFilterCategory>('all');

  const customShortcutIds = useMemo(
    () => new Set((customShortcuts ?? []).map((c) => c.id)),
    [customShortcuts]
  );

  const allShortcuts = useMemo(() => {
    const customAsResolved: ResolvedShortcut[] = (customShortcuts ?? []).map((chip) => ({
      id: chip.id,
      label: chip.label,
      query: chip.query,
      category: chip.category ?? 'plan',
      description: chip.description,
      ...(chip.teamShortcutId ? { teamShortcutId: chip.teamShortcutId } : {}),
    }));
    const marketShortcuts = getShortcutsForMarket(isUS ?? false);
    const resolved = resolveShortcuts(marketShortcuts, intl);
    const merged = [...customAsResolved, ...resolved];
    const dedupedByContent = new Map<string, ResolvedShortcut>();

    for (const shortcut of merged) {
      const dedupeKey = `${shortcut.label}::${shortcut.query}`;
      if (!dedupedByContent.has(dedupeKey)) {
        dedupedByContent.set(dedupeKey, shortcut);
      }
    }

    return Array.from(dedupedByContent.values());
  }, [customShortcuts, intl, isUS]);

  const fuse = useMemo(
    () =>
      new Fuse(allShortcuts, { keys: ['label', 'query', 'description'], threshold: 0.4 }),
    [allShortcuts]
  );

  const isShortcutPinned = useCallback(
    (shortcut: ResolvedShortcut) =>
      shortcut.id.startsWith('default-')
        ? (pinnedShortcutIds?.has(shortcut.id) ?? false)
        : (pinnedShortcutLabels?.has(shortcut.label) ?? false),
    [pinnedShortcutIds, pinnedShortcutLabels]
  );

  // Discovery-first sort: unsaved built-in → saved built-in → custom
  const sortedShortcuts = useMemo(() => {
    return [...allShortcuts].sort((a, b) => {
      const tierA = customShortcutIds.has(a.id) ? 2 : isShortcutPinned(a) ? 1 : 0;
      const tierB = customShortcutIds.has(b.id) ? 2 : isShortcutPinned(b) ? 1 : 0;
      return tierA - tierB;
    });
  }, [allShortcuts, customShortcutIds, isShortcutPinned]);

  const filteredShortcuts = useMemo(() => {
    // When searching, use Fuse relevance ranking (no discovery sort)
    if (searchQuery) {
      const results = fuse.search(searchQuery).map((r) => r.item);
      if (selectedFilter === 'all') return results;
      if (selectedFilter === 'saved') return results.filter(isShortcutPinned);
      if (selectedFilter === 'my_shortcuts')
        return results.filter((s) => customShortcutIds.has(s.id));
      return results.filter((s) => s.browseCategory === selectedFilter);
    }
    // When browsing, use discovery-first sort
    if (selectedFilter === 'all') return sortedShortcuts;
    if (selectedFilter === 'saved') return sortedShortcuts.filter(isShortcutPinned);
    if (selectedFilter === 'my_shortcuts')
      return sortedShortcuts.filter((s) => customShortcutIds.has(s.id));
    return sortedShortcuts.filter((s) => s.browseCategory === selectedFilter);
  }, [
    searchQuery,
    fuse,
    selectedFilter,
    sortedShortcuts,
    isShortcutPinned,
    customShortcutIds,
  ]);

  const resetBrowseState = useCallback(() => {
    setSearchQuery('');
    setSelectedFilter('all');
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    selectedFilter,
    setSelectedFilter,
    customShortcutIds,
    allShortcuts,
    filteredShortcuts,
    isShortcutPinned,
    resetBrowseState,
  };
}
